import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Bookmark, Trash2, ArrowRight, Calendar } from "lucide-react";
import { useInspiration } from "../hooks/useStore";

export default function InspirationDrawer({ isOpen, onClose }) {
  const { list, dispatch } = useInspiration();
  const navigate = useNavigate();

  const waMessage = encodeURIComponent(
    "Hello Ananda,\n\nI'd like to discuss the following designs in a consultation:\n" +
      list
        .map((i, n) => `${n + 1}. ${i.name}${i.finish ? ` (${i.finish})` : ""}`)
        .join("\n") +
      "\n\nWhen would be a good time to talk?"
  );

  const goBook = () => {
    onClose();
    navigate("/book", { state: { fromInspiration: true } });
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-matte-900/50 z-50 transition-opacity duration-300 ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 bottom-0 w-full sm:w-96 bg-cream-50 z-50 shadow-2xl flex flex-col transition-transform duration-400 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-cream-200">
          <div className="flex items-center gap-3">
            <Bookmark size={18} className="text-walnut-500" />
            <span className="font-serif text-lg text-matte-900">Inspiration List</span>
            {list.length > 0 && (
              <span className="bg-walnut-100 text-walnut-600 text-xs font-sans px-2 py-0.5 rounded-full">
                {list.length}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-matte-400 hover:text-matte-700 transition-colors"
            aria-label="Close inspiration list"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {list.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center gap-5">
              <Bookmark size={36} className="text-matte-300" />
              <div>
                <p className="font-serif text-xl text-matte-500 mb-2">No designs saved yet</p>
                <p className="font-sans text-sm text-matte-400 leading-relaxed">
                  Save reference designs as you browse the portfolio. Bring the
                  list to your consultation — Ananda will use it to understand
                  your taste.
                </p>
              </div>
              <Link
                to="/portfolio"
                onClick={onClose}
                className="font-sans text-sm border border-walnut-300 text-walnut-600 px-5 py-2.5 hover:bg-walnut-500 hover:text-white hover:border-walnut-500 transition-colors"
              >
                Browse Portfolio
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="font-sans text-xs text-matte-400 leading-relaxed mb-5">
                These are the reference designs you'd like to discuss with
                Ananda. Bring this list into your free consultation — he'll use
                them as starting points for your bespoke pieces.
              </p>
              {list.map((item) => (
                <div
                  key={`${item.id}-${item.finish || ""}`}
                  className="flex gap-4 bg-white border border-cream-200 rounded-sm p-3"
                >
                  <Link
                    to={item.slug ? `/portfolio/${item.slug}` : "/portfolio"}
                    onClick={onClose}
                    className="flex-shrink-0"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-sm"
                      loading="lazy"
                      decoding="async"
                      width="64"
                      height="64"
                    />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={item.slug ? `/portfolio/${item.slug}` : "/portfolio"}
                      onClick={onClose}
                    >
                      <h4 className="font-serif text-sm text-matte-900 leading-snug line-clamp-1 hover:text-walnut-600 transition-colors">
                        {item.name}
                      </h4>
                    </Link>
                    <p className="font-sans text-xs text-matte-400 capitalize mt-0.5">
                      {item.category?.replace(/-/g, " ")}
                    </p>
                    {item.finish && (
                      <p className="font-sans text-xs text-walnut-500 mt-1">
                        Reference finish: {item.finish}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() =>
                      dispatch({ type: "REMOVE", id: item.id, finish: item.finish })
                    }
                    className="text-matte-300 hover:text-red-400 transition-colors flex-shrink-0 self-start mt-0.5"
                    aria-label={`Remove ${item.name} from inspiration list`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => dispatch({ type: "CLEAR" })}
                className="text-xs font-sans text-matte-400 hover:text-matte-700 underline mt-2"
              >
                Clear list
              </button>
            </div>
          )}
        </div>

        {list.length > 0 && (
          <div className="border-t border-cream-200 px-6 py-5 space-y-3">
            <p className="font-sans text-xs text-matte-400 text-center">
              Ready to talk? Choose how to start the consultation:
            </p>

            <button
              onClick={goBook}
              className="w-full flex items-center justify-center gap-2 bg-matte-900 text-cream-100 font-sans text-sm tracking-wide py-3.5 hover:bg-walnut-700 transition-colors rounded-sm"
            >
              <Calendar size={16} />
              Book a Free Consultation
            </button>

            <a
              href={`https://wa.me/9779841488271?text=${waMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 border border-[#25D366] text-[#25D366] font-sans text-sm tracking-wide py-3 hover:bg-[#25D366] hover:text-white transition-colors rounded-sm"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              WhatsApp Ananda
            </a>

            <Link
              to="/portfolio"
              onClick={onClose}
              className="w-full flex items-center justify-center gap-2 font-sans text-xs text-matte-500 hover:text-matte-900 py-2"
            >
              Continue browsing
              <ArrowRight size={12} />
            </Link>
          </div>
        )}
      </div>
    </>
  );
}
